import { useState, useEffect, useCallback } from 'react';
import { CreditCard, FileText } from 'lucide-react';
import { dispatchFetch, T, F, bd, fmtDate, fmtMoney } from '@/lib/dispatchApi';
import DispatchButton from '@/components/dispatch/DispatchButton';

export default function DispatchPayoutsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchPayouts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await dispatchFetch('/api/payouts');
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setData(await res.json());
    } catch (e) {
      console.error('Payouts fetch', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPayouts(); }, [fetchPayouts]);

  const handleExport = async () => {
    if (!data) return;
    const lines = [
      'Bricks payout export',
      `Week total: ${fmtMoney(data.week_total)}`,
      `Paid: ${fmtMoney(data.paid_amount)} · Pending: ${fmtMoney(data.pending_amount)}`,
      '',
      ...(data.recent_jobs || []).map(j =>
        `${fmtDate(j.scheduled_date)} · ${j.address} · ${fmtMoney(j.payout_amount)} · ${j.payout_status}`
      ),
    ].join('\n');

    if (navigator.share) {
      await navigator.share({ title: 'Bricks payout export', text: lines }).catch(() => {});
    } else {
      await navigator.clipboard.writeText(lines).catch(() => {});
      alert('Copied to clipboard');
    }
  };

  const paidRatio = data && data.week_total > 0 ? data.paid_amount / data.week_total : 0;
  const px = 24;

  return (
    <div style={{ backgroundColor: T.night, minHeight: '100%', paddingLeft: px, paddingRight: px, paddingTop: 16, paddingBottom: 32, color: T.moon, fontFamily: F.heading }}>
      <p style={{ fontSize: 26, color: T.moon, fontFamily: F.heading, letterSpacing: -0.4, marginBottom: 8 }}>Payouts</p>
      <p style={{ fontFamily: F.mono, fontSize: 11, color: T.moonFaint, letterSpacing: 0.4, marginBottom: 20 }}>
        {data?.payout_label || (data?.payout_model === 'owner_full' ? 'Full package · $0 deduction' : 'Fixed rate card')}
      </p>

      {/* Weekly card */}
      <div style={{ backgroundColor: T.nightSoft, ...bd(T.nightHairS), borderRadius: 2, padding: 20, marginBottom: 28 }}>
        <p style={{ fontSize: 13, fontWeight: 500, color: T.moonMute, marginBottom: 10 }}>This week</p>
        <p style={{ fontFamily: F.mono, fontSize: 36, color: T.moon, letterSpacing: -0.8, marginBottom: 4 }}>{fmtMoney(data?.week_total ?? 0)}</p>
        <p style={{ fontSize: 11, color: T.moonFaint, marginBottom: 16 }}>
          Pays out {data?.payout_day ?? 'Monday'} · {data?.week_job_count ?? 0} job{(data?.week_job_count ?? 0) === 1 ? '' : 's'}
        </p>
        <div style={{ height: 3, borderRadius: 2, overflow: 'hidden', display: 'flex', backgroundColor: T.nightHair, marginBottom: 8 }}>
          <div style={{ flex: paidRatio, backgroundColor: T.moon }} />
          <div style={{ flex: 1 - paidRatio, backgroundColor: T.moonFaint }} />
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <span style={{ fontSize: 12, color: T.moonFaint }}>Paid <span style={{ fontFamily: F.mono }}>{fmtMoney(data?.paid_amount ?? 0)}</span></span>
          <span style={{ fontSize: 12, color: T.moonFaint }}>Pending <span style={{ fontFamily: F.mono }}>{fmtMoney(data?.pending_amount ?? 0)}</span></span>
        </div>
      </div>

      {/* Recent jobs */}
      <div style={{ marginBottom: 24 }}>
        <p style={{ fontSize: 13, fontWeight: 500, color: T.moonFaint, marginBottom: 12 }}>Recent jobs</p>
        <div style={{ backgroundColor: T.nightSoft, ...bd(T.nightHair), borderRadius: 2 }}>
          {(data?.recent_jobs ?? []).slice(0, 8).map((job, i, arr) => (
            <div key={job.id} style={{ display: 'flex', alignItems: 'center', padding: '14px 16px', ...(i < arr.length - 1 ? { borderBottom: `1px solid ${T.nightHair}` } : {}) }}>
              <div style={{ flex: 1 }}>
                <p style={{ fontSize: 13, color: T.moon, marginBottom: 3 }}>{job.address}</p>
                <p style={{ fontFamily: F.mono, fontSize: 11, color: T.moonFaint, margin: 0 }}>{fmtDate(job.scheduled_date)}</p>
              </div>
              <div style={{ textAlign: 'right' }}>
                <p style={{ fontFamily: F.mono, fontSize: 13, color: T.moon, marginBottom: 3 }}>{fmtMoney(job.payout_amount)}</p>
                <p style={{ fontSize: 12, color: /fail|held|declin/i.test(job.payout_status || '') ? T.badText : T.moonMute, margin: 0 }}>
                  {job.payout_status}
                </p>
              </div>
            </div>
          ))}
          {(data?.recent_jobs?.length ?? 0) === 0 && (
            <div style={{ padding: 20, textAlign: 'center' }}>
              <p style={{ fontSize: 13, color: T.moonFaint }}>No jobs yet this period</p>
            </div>
          )}
        </div>
      </div>

      {/* Payout actions — honest empty states, no fake Connected bank */}
      <div style={{ marginBottom: 24 }}>
        <p style={{ fontSize: 13, fontWeight: 500, color: T.moonFaint, marginBottom: 12 }}>Payout</p>
        <div style={{ backgroundColor: T.nightSoft, ...bd(T.nightHair), borderRadius: 2 }}>
          <div style={{ display: 'flex', alignItems: 'center', padding: '14px 16px', gap: 12, borderBottom: `1px solid ${T.nightHair}` }}>
            <div style={{ width: 28, display: 'flex', justifyContent: 'center' }}>
              <CreditCard size={15} color={T.moonMute} strokeWidth={1.5} />
            </div>
            <div style={{ flex: 1 }}>
              <p style={{ fontSize: 14, color: T.moon, marginBottom: 2 }}>Payout method</p>
              <p style={{ fontSize: 11, color: T.moonFaint, margin: 0 }}>Connect payouts to receive deposits</p>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', padding: '14px 16px', gap: 12 }}>
            <div style={{ width: 28, display: 'flex', justifyContent: 'center' }}>
              <FileText size={15} color={T.moonMute} strokeWidth={1.5} />
            </div>
            <div style={{ flex: 1 }}>
              <p style={{ fontSize: 14, color: T.moon, marginBottom: 2 }}>Tax documents</p>
              <p style={{ fontSize: 11, color: T.moonFaint, margin: 0 }}>No tax docs yet</p>
            </div>
          </div>
        </div>
      </div>

      {/* Export */}
      <div style={{ marginBottom: 24 }}>
        <DispatchButton variant="main" full icon={FileText} data-testid="payouts-export" onClick={handleExport} disabled={!data}>Export earnings</DispatchButton>
      </div>
    </div>
  );
}
